import { useCallback, useContext, useMemo } from 'react'

import { indexes } from '@mab-common/utils'

import classnames from 'classnames'

import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import type {
  TSearchAnalystEventParams,
  TSearchAnalystEventProps,
} from '~/services/elastic/analyst'
import useSearchAnalyst, {
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import analyticsPusher, { analyticsEventTypes } from '~/utils/analyticsPusher'
import { toURL } from '~/utils/urlResolver'

import ArtworkPrice from '../ArtworkPrice/ArtworkPrice'
import type { ImageProps } from '../Image/Image'
import Link from '../Link/Link'
import {
  MarketActivityLabel,
  MarketActivityType,
} from '../MarketActivityLabel/MarketActivityLabel'
import SpecificImage from '../SpecificImage/SpecificImage'
import Text, { TextSizes } from '../Text/Text'

import styles from './ArtworkCard.module.css'
import EsIndexTypes = indexes.EsIndexTypes

import ArtworkCardPortfolio from '../../blocks/ArtworkCardPortfolio/ArtworkCardPortfolio'

export enum ArtWorkCardVariants {
  primary = 'primary',
  magazine = 'magazine',
}
interface MarketActivity {
  type: MarketActivityType
  count: number
}

function getMarketActivity(
  sellTicketsCount?: number,
  ownedTicketsCount?: number,
  buyTicketsCount?: number
): MarketActivity | null {
  if (sellTicketsCount)
    return {
      type: MarketActivityType.PublicAuctionCount,
      count: sellTicketsCount,
    }
  if (ownedTicketsCount)
    return { type: MarketActivityType.InNetworkCount, count: ownedTicketsCount }
  if (buyTicketsCount)
    return { type: MarketActivityType.CollectorsWant, count: buyTicketsCount }
  return null
}

export interface currencyOptions {
  _key: string
  iso: string
  price: number
}

export interface ArtworkCardProps {
  _id: string
  artworkId?: string
  image: ImageProps
  title: string
  artworkSlug: string
  artistSlug: string
  signed: string
  collectionSlug?: string
  collectionTaxonomy?: string
  artist?: string
  artistImage?: string
  artistId?: string
  format?: string
  variant?: ArtWorkCardVariants
  className?: string
  smallOnMobile?: boolean
  insightsQueryID?: string
  itemIndex?: number
  qualifiedUrl?: string
  artistUrl?: string
  type?: string
  listPriceMin?: number
  listPriceMax?: number
  listPriceMinValues?: currencyOptions[]
  listPriceMaxValues?: currencyOptions[]
  year?: string
  poa?: boolean
  valueText?: any
  aagr?: number
  buyTicketsCount?: number
  sellTicketsCount?: number
  ownedTicketsCount?: number
  testID?: string
  searchInsightsEvents?: {
    artwork?: TSearchAnalystEventProps
    artist?: TSearchAnalystEventProps
  }
  noLazyLoad?: boolean
  noArtistLink?: boolean
  imageUrl?: string
  agnostic?: boolean
  handleAnalytics?: () => void
}

export default function ArtworkCard({
  _id,
  image,
  title,
  artworkSlug,
  artistSlug,
  collectionSlug,
  collectionTaxonomy,
  artist,
  format,
  variant = ArtWorkCardVariants.primary,
  className,
  smallOnMobile,
  itemIndex,
  insightsQueryID,
  listPriceMin,
  listPriceMax,
  listPriceMinValues,
  listPriceMaxValues,
  poa,
  year,
  aagr,
  buyTicketsCount,
  sellTicketsCount,
  ownedTicketsCount,
  testID,
  searchInsightsEvents,
  noLazyLoad = false,
  noArtistLink = false,
  handleAnalytics,
  artistId,
}: ArtworkCardProps) {
  const marketActivity = getMarketActivity(
    sellTicketsCount,
    ownedTicketsCount,
    buyTicketsCount
  )

  const themes = {
    primary: {
      title: {
        size: TextSizes.secondaryTitleLrg,
      },
      artist: {
        size: TextSizes.primaryTitleSml,
      },
      format: {
        size: TextSizes.bodySml,
      },
    },
    magazine: {
      title: {
        size: TextSizes.secondaryTitleLrg,
      },
      artist: {
        size: TextSizes.primaryTitleSml,
      },
      format: {
        size: TextSizes.bodySml,
      },
    },
  }
  const { stateDispatch } = useContext(GlobalStoreContext)
  const { sendSearchInsightsEvent } = useSearchAnalyst()

  const artworkSearchEventParams: TSearchAnalystEventParams = useMemo(() => {
    const args: TSearchAnalystEventParams = {
      indexType: EsIndexTypes.ARTWORKS,
      eventName: searchInsightsEvents?.artwork?.name as any,
      objectIDs: [_id],
    }
    if (
      searchInsightsEvents?.artwork?.method ===
      SearchAnalystMethods.clickedObjectIDsAfterSearch
    ) {
      args.queryID = insightsQueryID
      args.positions = itemIndex ? [itemIndex] : []
    }
    return args
  }, [_id, insightsQueryID, itemIndex, searchInsightsEvents?.artwork])

  const onArtworkClick = useCallback(() => {
    // Send even to GA.
    analyticsPusher(analyticsEventTypes.gaEvent, {
      gaEventName: 'Artwork card clicked',
    })

    if (handleAnalytics) {
      handleAnalytics()
    }

    if (searchInsightsEvents?.artwork)
      sendSearchInsightsEvent(
        searchInsightsEvents.artwork.method,
        artworkSearchEventParams
      )
    if (insightsQueryID)
      stateDispatch({
        type: GlobalStoreProps.UPDATE_ALGOLIA_CURRENT_QUERY_ID,
        payload: { insightsQueryID, artworkID: _id },
      })
  }, [
    _id,
    insightsQueryID,
    searchInsightsEvents?.artwork,
    artworkSearchEventParams,
  ])

  const onArtistClick = useCallback(() => {
    if (searchInsightsEvents?.artist)
      sendSearchInsightsEvent(searchInsightsEvents.artist.method, {
        eventName: searchInsightsEvents.artist.name,
        indexType: EsIndexTypes.ARTIST,
        filters: [`artistSlug:${artistSlug}`],
      })
  }, [])

  return (
    <div
      className={classnames(
        styles.base,
        className && className,
        smallOnMobile && styles.smallOnMobile
      )}
      data-testid={testID}
      data-test="artwork-card"
    >
      <Link
        href={toURL({
          slug: artworkSlug,
          artist: artistSlug,
          collection: collectionSlug,
          collectionTaxonomy: collectionTaxonomy,
          _type: 'artwork',
        })}
        noPrefetch
        onClick={onArtworkClick}
        className={styles.link}
        testAttr="artwork-card-link"
      >
        <div className={styles.artistDetails}>
          <div className={styles.imageContainer}>
            <div className={styles.imageContainerInner}>
              {image && image.asset && (
                <SpecificImage
                  asset={image.asset}
                  alt={`${title} - ${format} by ${artist} ${year} - MyArtBroker`}
                  width={400}
                  noLazyLoad={noLazyLoad}
                />
              )}
            </div>
          </div>

          <div className={styles.details}>
            <Text
              size={themes[variant].title.size}
              italic={true}
              className={styles.title}
            >
              {title}&nbsp;
            </Text>
          </div>
        </div>
      </Link>

      <div className={classnames(styles.details, styles.priceDetails)}>
        {!noArtistLink && (
          <Link
            href={toURL({
              slug: artistSlug,
              _type: 'artist',
            })}
            onClick={onArtistClick}
            className={styles.fadeLink}
            noPrefetch
          >
            <Text size={themes[variant].artist.size} className={styles.artist}>
              {artist}
            </Text>
          </Link>
        )}
        <Link
          href={toURL({
            slug: artworkSlug,
            artist: artistSlug,
            collection: collectionSlug,
            collectionTaxonomy: collectionTaxonomy,
            _type: 'artwork',
          })}
          noPrefetch
          onClick={onArtworkClick}
          className={styles.link}
        >
          {noArtistLink && (
            <Text size={themes[variant].artist.size} className={styles.artist}>
              {artist}
            </Text>
          )}
          {format && (
            <Text size={themes[variant].format.size} className={styles.format}>
              {format}&nbsp;
            </Text>
          )}
          <div className={styles.statContainer}>
            <div className={styles.priceContainer}>
              <div className={styles.artworkCardPrice}>
                <ArtworkPrice
                  listPriceMin={listPriceMin}
                  listPriceMax={listPriceMax}
                  listPriceMinValues={listPriceMinValues}
                  listPriceMaxValues={listPriceMaxValues}
                  poa={poa}
                />
              </div>
              {/* Commented out AAGR during bad market times */}
              {/* {aagr ? ( */}
              {/*   <div className={styles.aagr}> */}
              {/*     <GrowthStatus */}
              {/*       size="medium" */}
              {/*       growth={+aagr} */}
              {/*       title="AAGR (5YR)" */}
              {/*       hideGraph */}
              {/*     /> */}
              {/*   </div> */}
              {/* ) : ( */}
              {/*   '' */}
              {/* )} */}
            </div>
          </div>
        </Link>
      </div>
      {(marketActivity || artistId) && (
        <div className={styles.statusRow}>
          {marketActivity && (
            <Link
              href={toURL({
                slug: artworkSlug,
                artist: artistSlug,
                collection: collectionSlug,
                collectionTaxonomy: collectionTaxonomy,
                _type: 'artwork',
              })}
              noPrefetch
              onClick={onArtworkClick}
            >
              <MarketActivityLabel
                type={marketActivity.type}
                count={marketActivity.count}
                size="large"
              />
            </Link>
          )}
          {artistId && (
            <ArtworkCardPortfolio
              artworkId={_id}
              artworkImage={image}
              artistId={artistId}
              artistName={artist}
              artworkName={title}
              eventLocation="Artwork Tile"
              artworkDetails={{
                _id,
                image,
                title,
                artworkSlug,
                artistSlug,
                collectionSlug,
                collectionTaxonomy,
                artist,
                format,
                listPriceMin,
                listPriceMax,
                listPriceMinValues,
                listPriceMaxValues,
                poa,
                year,
                aagr,
                buyTicketsCount,
                sellTicketsCount,
                ownedTicketsCount,
                artistId,
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
